import {
  AgentModelParam,
  AgentRunStatus,
} from '../components/agentChat/AgentChat.types';
import {
  END_NODE_ID,
  START_NODE_ID,
  ensureLinearWorkflow,
  getLinearActionNodes,
  normalizeGraphNodes,
} from '../automation/workflowGraph';

export type AutomationTriggerType = 'manual' | 'file_added' | 'schedule';

export type AutomationScheduleFrequency = 'hourly' | 'daily' | 'weekly';

export const AUTOMATION_SCHEDULE_FREQUENCIES: {
  value: AutomationScheduleFrequency;
  label: string;
}[] = [
  { value: 'hourly', label: 'Every hour' },
  { value: 'daily', label: 'Every day' },
  { value: 'weekly', label: 'Every week' },
];

export const AUTOMATION_WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

export type AutomationExecutionStatus = 'success' | 'error' | 'running';

export interface AutomationExecutionEntry {
  id: string;
  startedAt: string;
  finishedAt?: string | null;
  status: AutomationExecutionStatus;
  message?: string;
  filePath?: string | null;
  branchTaken?: Record<string, 'true' | 'false'>;
}

export interface AutomationTrigger {
  type: AutomationTriggerType;
  config: Record<string, unknown>;
}

export type AutomationActionType =
  | 'copy_move_file'
  | 'add_to_serato_crate'
  | 'read_write_tags'
  | 'import_to_library'
  | 'if_else'
  | 'run_agent';

export type AutomationNodeType =
  | 'start'
  | 'end'
  | 'trigger'
  | AutomationActionType;

export interface AutomationGraphNode {
  id: string;
  type: AutomationNodeType;
  position: { x: number; y: number };
  config: Record<string, unknown>;
}

export interface AutomationGraphEdge {
  id: string;
  from: string;
  to: string;
}

export interface AutomationGraph {
  nodes: AutomationGraphNode[];
  edges: AutomationGraphEdge[];
}

/** Saved workflow the Automations page loads and edits. */
export interface AutomationWorkflow {
  id: number;
  name: string;
  trigger: AutomationTrigger;
  graph: AutomationGraph;
  enabled: boolean;
  instructions: string;
  model?: string | null;
  modelParams?: AgentModelParam[] | null;
  runCount: number;
  lastRunAt?: string | null;
  lastRunId?: string | null;
  lastStatus?: AgentRunStatus | null;
  executionLog?: AutomationExecutionEntry[];
  createdAt: string;
}

export interface AutomationActionDefinition {
  type: AutomationActionType;
  label: string;
  description: string;
}

export const AUTOMATION_TRIGGER_LABELS: Record<AutomationTriggerType, string> =
  {
    manual: 'Manual',
    file_added: 'File added',
    schedule: 'Schedule',
  };

export const AUTOMATION_ACTION_CATALOG: AutomationActionDefinition[] = [
  {
    type: 'copy_move_file',
    label: 'Copy / move file',
    description: 'Copy or move a file to a folder you choose.',
  },
  {
    type: 'add_to_serato_crate',
    label: 'Add to Serato crate',
    description: 'Add a track to a Serato crate you name.',
  },
  {
    type: 'read_write_tags',
    label: 'Read / write tags',
    description: 'Read or update ID3 tags on a file.',
  },
  {
    type: 'import_to_library',
    label: 'Import to library',
    description: 'Index a file in your Crate Hackers library.',
  },
  {
    type: 'if_else',
    label: 'If / else',
    description: 'Split the workflow based on BPM or other conditions.',
  },
  {
    type: 'run_agent',
    label: 'Run Ask Crate Hackers',
    description: 'Run a saved prompt through the chat agent.',
  },
];

export const createBlankAutomationGraph = (
  trigger: AutomationTrigger = {
    type: 'file_added',
    config: {
      extensions: 'mp3, wav, flac, aiff, m4a',
      includeSubfolders: true,
    },
  },
): AutomationGraph => normalizeGraphNodes({ nodes: [], edges: [] }, trigger);

export const migrateLegacyAutomationToGraph = (input: {
  instructions: string;
  model?: string | null;
}): AutomationGraph =>
  normalizeGraphNodes(
    {
      nodes: [
        {
          id: 'run-agent-1',
          type: 'run_agent',
          position: { x: 320, y: 160 },
          config: {
            instructions: input.instructions,
            model: input.model ?? null,
          },
        },
      ],
      edges: [
        { id: 'edge-start-agent', from: START_NODE_ID, to: 'run-agent-1' },
        { id: 'edge-agent-end', from: 'run-agent-1', to: END_NODE_ID },
      ],
    },
    { type: 'manual', config: {} },
  );

export const normalizeAutomationWorkflow = (row: {
  id: number;
  name: string;
  trigger?: AutomationTrigger | null;
  graph?: AutomationGraph | null;
  enabled?: boolean;
  instructions: string;
  model?: string | null;
  modelParams?: AgentModelParam[] | null;
  runCount: number;
  lastRunAt?: string | null;
  lastRunId?: string | null;
  lastStatus?: AgentRunStatus | null;
  executionLog?: AutomationExecutionEntry[] | null;
  createdAt: string;
}): AutomationWorkflow => {
  const instructions = row.instructions ?? '';
  const trigger = row.trigger ?? { type: 'manual' as const, config: {} };
  const rawGraph =
    row.graph ??
    (instructions.trim()
      ? migrateLegacyAutomationToGraph({
          instructions,
          model: row.model,
        })
      : createBlankAutomationGraph(trigger));
  const graph = ensureLinearWorkflow(normalizeGraphNodes(rawGraph, trigger));

  return {
    id: row.id,
    name: row.name,
    trigger,
    graph,
    enabled: row.enabled ?? false,
    instructions,
    model: row.model ?? null,
    modelParams: row.modelParams ?? null,
    runCount: row.runCount ?? 0,
    lastRunAt: row.lastRunAt ?? null,
    lastRunId: row.lastRunId ?? null,
    lastStatus: row.lastStatus ?? null,
    executionLog: row.executionLog ?? [],
    createdAt: row.createdAt,
  };
};

export const getActionLabel = (type: AutomationActionType): string =>
  AUTOMATION_ACTION_CATALOG.find((action) => action.type === type)?.label ??
  type;

export const getOrderedActionNodes = (
  graph: AutomationGraph | null | undefined,
): AutomationGraphNode[] => getLinearActionNodes(graph);

export {
  appendLinearAction as appendActionNode,
  removeLinearAction as removeActionNode,
} from '../automation/workflowGraph';

export const updateNodeConfig = (
  graph: AutomationGraph,
  nodeId: string,
  config: Record<string, unknown>,
): AutomationGraph => ({
  ...graph,
  nodes: graph.nodes.map((node) =>
    node.id === nodeId
      ? { ...node, config: { ...node.config, ...config } }
      : node,
  ),
});

export const updateTriggerOnGraph = (
  graph: AutomationGraph,
  trigger: AutomationTrigger,
): AutomationGraph => normalizeGraphNodes(graph, trigger);

export const getNodeDisplayLabel = (node: AutomationGraphNode): string => {
  if (node.type === 'start' || node.type === 'trigger') {
    const triggerType =
      (node.config.triggerType as AutomationTriggerType) ?? 'manual';
    return `Start · ${AUTOMATION_TRIGGER_LABELS[triggerType]}`;
  }
  if (node.type === 'end') return 'End';
  return getActionLabel(node.type);
};

export const extractTriggerFromGraph = (
  graph: AutomationGraph,
): AutomationTrigger => {
  const start = graph.nodes.find(
    (node) => node.type === 'start' || node.type === 'trigger',
  );
  const triggerType =
    (start?.config.triggerType as AutomationTriggerType) ?? 'manual';
  const { triggerType: _ignored, ...config } = start?.config ?? {};
  return { type: triggerType, config };
};
